import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";

/**
 * Hook to generate localized paths with language prefix.
 * Example: useLocalizedPath() => { localePath("/blog") => "/fr/blog" }
 */
export const useLocalizedPath = () => {
  const { language } = useLanguage();

  const localePath = useCallback(
    (path: string) => {
      // Don't prefix admin routes
      if (path.startsWith("/admin")) return path;
      // Remove leading slash for joining
      const cleanPath = path.startsWith("/") ? path.slice(1) : path;
      return `/${language}${cleanPath ? `/${cleanPath}` : ""}`;
    },
    [language]
  );

  return { localePath, language };
};

/**
 * Get the path without the language prefix.
 * Example: stripLangPrefix("/fr/blog") => "/blog"
 */
export const stripLangPrefix = (path: string): string => {
  const match = path.match(/^\/(fr|en)(\/.*)?$/);
  if (match) {
    return match[2] || "/";
  }
  return path;
};

/**
 * Extract language from path.
 * Example: getLangFromPath("/fr/blog") => "fr"
 */
export const getLangFromPath = (path: string): "fr" | "en" | null => {
  const match = path.match(/^\/(fr|en)(\/|$)/);
  if (match) {
    return match[1] as "fr" | "en";
  }
  return null;
};
